import React, { useState, useEffect, useRef } from 'react';
import { Leaf, Heart, Globe, Shield } from 'lucide-react';
import { useCountry, type ValueIcon } from '@/contexts/CountryContext';

const ICONS: Record<ValueIcon, React.ComponentType<{ className?: string }>> = {
  heart: Heart,
  leaf: Leaf,
  globe: Globe,
  shield: Shield,
};

const AnimatedCounter: React.FC<{ target: number; suffix: string; isVisible: boolean }> = ({
  target,
  suffix,
  isVisible,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const AboutSection: React.FC = () => {
  const { content } = useCountry();
  const { stats, values } = content;
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 lg:py-32 bg-[#FAF8F3]" ref={sectionRef}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Main About */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-24">
          {/* Image Side */}
          <div className="relative">
            <div className="img-zoom aspect-[4/5]">
              <img
                src={content.aboutImage}
                alt={content.aboutImageAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-8 -right-4 lg:-right-8 bg-[#1A3B5C] p-8 max-w-[240px]">
              <span className="font-serif text-4xl text-[#C9A961] block mb-2">{content.aboutAccentLabel}</span>
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/70">
                {content.aboutAccentTagline}
              </span>
            </div>
          </div>

          {/* Text Side */}
          <div>
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
              Our Philosophy
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A3B5C] font-light mb-8 leading-tight">
              The Art of<br />Transformative Travel
            </h2>
            <div className="space-y-4 mb-10">
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                At Zera, we believe that travel at its finest is not merely about destinations; it is about the profound transformation that occurs when extraordinary places meet extraordinary care.
              </p>
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                Founded on the principle that luxury should be both deeply personal and environmentally conscious, we curate experiences that honor the natural beauty and cultural richness of every destination we touch.
              </p>
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                Our dedicated concierge team works tirelessly to anticipate your every need, crafting bespoke itineraries that reflect your unique vision of the perfect journey.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24 py-12 border-y border-[#E8DFD0]">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <span className="font-serif text-4xl md:text-5xl text-[#1A3B5C] block mb-2">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} isVisible={isVisible} />
              </span>
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999]">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="text-center mb-14">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            What Guides Us
          </span>
          <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light gold-underline">
            Our Values
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((val) => {
            const Icon = ICONS[val.icon];
            return (
              <div key={val.title} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#C9A961]/30 flex items-center justify-center group-hover:bg-[#1A3B5C] group-hover:border-[#1A3B5C] transition-all duration-300">
                  <Icon className="w-6 h-6 text-[#C9A961] group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-serif text-xl text-[#1A3B5C] mb-3">{val.title}</h4>
                <p className="font-sans text-xs text-[#888] leading-relaxed">{val.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
